export enum SubAssemblyType {
  EMATDaqAmplifier = 'emat_daq_amplifier',
  EMATDaqFPGA = 'emat_daq_fpga',
  EMATSBC = 'emat_sbc',
  HandheldTransducer = 'handheld_transducer',
  IoTModem = 'iot_modem',
  LinearActuator = 'linear_actuator',
  Motor = 'motor',
  NucleoPCB = 'nucleo_pcb',
  PCBA = 'pcba',
  PrismHolder = 'prism_holder',
  RUGTransducer = 'rug_transducer',
  Wheel = 'wheel',
}

export const getSubAssemblyType = (value: string) => {
  if (Object.values(SubAssemblyType).includes(value as SubAssemblyType)) {
    return value as SubAssemblyType
  }
  return SubAssemblyType.LinearActuator
}

export const getSubAssemblyTypeLabel = (type: SubAssemblyType): string => {
  const labels: Record<SubAssemblyType, string> = {
    [SubAssemblyType.EMATDaqAmplifier]: 'EMAT DAQ Amplifier',
    [SubAssemblyType.EMATDaqFPGA]: 'EMAT DAQ FPGA',
    [SubAssemblyType.EMATSBC]: 'EMAT SBC',
    [SubAssemblyType.HandheldTransducer]: 'Handheld Transducer',
    [SubAssemblyType.IoTModem]: 'IoT Modem',
    [SubAssemblyType.LinearActuator]: 'Linear Actuator',
    [SubAssemblyType.Motor]: 'Motor',
    [SubAssemblyType.NucleoPCB]: 'Nucleo PCB',
    [SubAssemblyType.PCBA]: 'PCBA',
    [SubAssemblyType.PrismHolder]: 'Prism Holder',
    [SubAssemblyType.RUGTransducer]: 'RUG Transducer',
    [SubAssemblyType.Wheel]: 'Wheel',
  }
  return labels[type] ?? type
}

export enum InventoryIntent {
  Demo = 'demo',
  FieldUse = 'field_use',
  RandD = 'r&d',
  InHouseTesting = 'in_house_testing',
  FDEDeployment = 'fde_deployment',
}

export const getInventoryIntent = (value: string) => {
  if (Object.values(InventoryIntent).includes(value as InventoryIntent)) {
    return value as InventoryIntent
  }
  return InventoryIntent.Demo
}

export const getInventoryIntentLabel = (intent: InventoryIntent): string => {
  const labels: Record<InventoryIntent, string> = {
    [InventoryIntent.Demo]: 'Demo',
    [InventoryIntent.FieldUse]: 'Field Use',
    [InventoryIntent.RandD]: 'R&D',
    [InventoryIntent.InHouseTesting]: 'In-House Testing',
    [InventoryIntent.FDEDeployment]: 'FDE Deployment',
  }
  return labels[intent] ?? intent
}

export enum DepartmentOwner {
  EngineeringEkko = 'engineering_ekko',
  EngineeringFDE = 'engineering_fde',
  EngineeringHardware = 'engineering_hardware',
  EngineeringLocalization = 'engineering_localization',
  EngineeringSoftware = 'engineering_software',
  EngineeringTR = 'engineering_t&r',
  GovOperations = 'gov_operations',
  IT = 'it',
  Marketing = 'marketing',
  MechanicalTech = 'mechanical_tech',
  Operations = 'operations',
  Unknown = 'unknown',
}

export const getDepartmentOwner = (value: string) => {
  if (Object.values(DepartmentOwner).includes(value as DepartmentOwner)) {
    return value as DepartmentOwner
  }
  return DepartmentOwner.MechanicalTech
}

export const getDepartmentOwnerLabel = (owner: DepartmentOwner): string => {
  const labels: Record<DepartmentOwner, string> = {
    [DepartmentOwner.Operations]: 'Operations',
    [DepartmentOwner.EngineeringHardware]: 'Engineering - Hardware',
    [DepartmentOwner.EngineeringSoftware]: 'Engineering - Software',
    [DepartmentOwner.EngineeringEkko]: 'Engineering - Ekko',
    [DepartmentOwner.EngineeringTR]: 'Engineering - T&R',
    [DepartmentOwner.EngineeringLocalization]: 'Engineering - Localization',
    [DepartmentOwner.EngineeringFDE]: 'Engineering - FDE',
    [DepartmentOwner.MechanicalTech]: 'Mechanical Tech',
    [DepartmentOwner.Marketing]: 'Marketing',
    [DepartmentOwner.IT]: 'IT',
    [DepartmentOwner.GovOperations]: 'Gov Operations',
    [DepartmentOwner.Unknown]: 'Unknown',
  }
  return labels[owner] ?? owner
}

export enum SubAssemblyConnectorType {
  MCX = 'mcx',
  Threaded = 'threaded',
}

export const getSubAssemblyConnectorType = (value: string) => {
  if (
    Object.values(SubAssemblyConnectorType).includes(
      value as SubAssemblyConnectorType
    )
  ) {
    return value as SubAssemblyConnectorType
  }
  return undefined
}

export const getSubAssemblyConnectorTypeLabel = (
  type: SubAssemblyConnectorType
): string => {
  const labels: Record<SubAssemblyConnectorType, string> = {
    [SubAssemblyConnectorType.MCX]: 'MCX',
    [SubAssemblyConnectorType.Threaded]: 'Threaded',
  }
  return labels[type] ?? type
}

export enum WheelType {
  Crown = 'crown',
  LargeTube = 'large_tube',
  NAVIC = 'navic',
  NonMag = 'non_mag',
  SmallTube = 'small_tube',
  TankFloor = 'tank_floor',
  TankShell = 'tank_shell',
  TankShellNorth = 'tank_shell_north',
  TankShellSouth = 'tank_shell_south',
}

export const getWheelType = (value: string) => {
  if (Object.values(WheelType).includes(value as WheelType)) {
    return value as WheelType
  }
  return undefined
}

export const getWheelTypeLabel = (type: WheelType): string => {
  const labels: Record<WheelType, string> = {
    [WheelType.TankShellNorth]: 'Tank Shell - North',
    [WheelType.TankShellSouth]: 'Tank Shell - South',
    [WheelType.TankFloor]: 'Tank Floor',
    [WheelType.LargeTube]: 'Large Tube',
    [WheelType.SmallTube]: 'Small Tube',
    [WheelType.Crown]: 'Crown',
    [WheelType.NonMag]: 'Non-Mag',
    [WheelType.NAVIC]: 'NAVIC',
    [WheelType.TankShell]: 'Tank Shell',
  }
  return labels[type] ?? type
}

export enum EquipmentTypes {
  APIRadian = 'api_radian',
  APITripod = 'api_tripod',
  APIBaseStation = 'api_base_station',
  ARG = 'arg',
  Auxiliary = 'auxiliary',
  Barnacle = 'barnacle',
  BOFI = 'bofi',
  Degasser = 'degasser',
  EMAT = 'emat',
  EMATBackpack = 'emat_backpack',
  EMATBaseStation = 'emat_base_station',
  EMATJackery = 'emat_jackery',
  EMATLaptopCase = 'emat_laptop_case',
  EMATNavicCase = 'emat_navic_case',
  EMATRasterCase = 'emat_raster_case',
  EMATSpareParts = 'emat_spare_parts',
  EMATToolBox = 'emat_tool_box',
  EMATWalkdownKit = 'emat_walkdown_kit',
  EVA = 'eva',
  FallArrest = 'fall_arrest',
  FischerFMP30C = 'fischer_fmp_30c',
  Handheld38DLPlus = 'handheld_38_dl_plus',
  Laptop = 'laptop',
  Lifeline = 'lifeline',
  Pipebot = 'pipebot',
  Pump = 'pump',
  RTS = 'rts',
  RTSA = 'rts_a',
  RTSB = 'rts_b',
  RTSTripod = 'rts_tripod',
  Spatula = 'spatula',
  Toka3BaseStation = 'toka3_base_station',
  Toka3ISENS = 'toka3_isens',
  Toka3Robot = 'toka3_robot',
  Toka3Tether = 'toka3_tether',
  Toka4BaseStation = 'toka4_base_station',
  Toka4ISENS = 'toka4_isens',
  Toka4Robot = 'toka4_robot',
  Toka4TetherCase = 'toka4_tether_case',
  Toka5BaseStation = 'toka5_base_station',
  Toka5ISENS = 'toka5_isens',
  Toka5Robot = 'toka5_robot',
  TokaFlexISENS = 'toka_flex_isens',
  TokaFlexRobot = 'toka_flex_robot',
  TokaGZISENS = 'toka_gz_isens',
  TokaGZRobot = 'toka_gz_robot',
  TokaMLOTether = 'toka_mlo_tether',
  ToolBox = 'tool_box',
  VoltageTransformerPelican = 'voltage_transformer_pelican',
  ZED = 'zed',
}

export const getEquipmentType = (value: string) => {
  if (Object.values(EquipmentTypes).includes(value as EquipmentTypes)) {
    return value as EquipmentTypes
  }
  return EquipmentTypes.Toka4TetherCase
}

export const getEquipmentTypeLabel = (type: EquipmentTypes): string => {
  const labels: Record<EquipmentTypes, string> = {
    [EquipmentTypes.APIRadian]: 'API Radian',
    [EquipmentTypes.APITripod]: 'API Tripod',
    [EquipmentTypes.APIBaseStation]: 'API Base Station',
    [EquipmentTypes.ARG]: 'ARG',
    [EquipmentTypes.Auxiliary]: 'Auxiliary',
    [EquipmentTypes.Barnacle]: 'Barnacle',
    [EquipmentTypes.BOFI]: 'BOFI',
    [EquipmentTypes.Degasser]: 'Degasser',
    [EquipmentTypes.EMAT]: 'EMAT',
    [EquipmentTypes.EMATBackpack]: 'EMAT Backpack',
    [EquipmentTypes.EMATBaseStation]: 'EMAT Base Station',
    [EquipmentTypes.EMATJackery]: 'EMAT Jackery',
    [EquipmentTypes.EMATLaptopCase]: 'EMAT Laptop Case',
    [EquipmentTypes.EMATNavicCase]: 'EMAT Navic Case',
    [EquipmentTypes.EMATRasterCase]: 'EMAT Raster Case',
    [EquipmentTypes.EMATSpareParts]: 'EMAT Spare Parts',
    [EquipmentTypes.EMATToolBox]: 'EMAT Tool Box',
    [EquipmentTypes.EMATWalkdownKit]: 'EMAT Walkdown Kit',
    [EquipmentTypes.EVA]: 'EVA',
    [EquipmentTypes.FallArrest]: 'Fall Arrest',
    [EquipmentTypes.FischerFMP30C]: 'Fischer FMP 30C',
    [EquipmentTypes.Handheld38DLPlus]: 'Handheld 38DL Plus',
    [EquipmentTypes.Laptop]: 'Laptop',
    [EquipmentTypes.Lifeline]: 'Lifeline',
    [EquipmentTypes.Pipebot]: 'Pipebot',
    [EquipmentTypes.Pump]: 'Pump',
    [EquipmentTypes.RTS]: 'RTS',
    [EquipmentTypes.RTSA]: 'RTS A',
    [EquipmentTypes.RTSB]: 'RTS B',
    [EquipmentTypes.RTSTripod]: 'RTS Tripod',
    [EquipmentTypes.Spatula]: 'Spatula',
    [EquipmentTypes.Toka3BaseStation]: 'TOKA 3 Base Station',
    [EquipmentTypes.Toka3ISENS]: 'TOKA 3 ISENS',
    [EquipmentTypes.Toka3Robot]: 'TOKA 3 Robot',
    [EquipmentTypes.Toka3Tether]: 'TOKA 3 Tether',
    [EquipmentTypes.Toka4BaseStation]: 'TOKA 4 Base Station',
    [EquipmentTypes.Toka4ISENS]: 'TOKA 4 ISENS',
    [EquipmentTypes.Toka4Robot]: 'TOKA 4 Robot',
    [EquipmentTypes.Toka4TetherCase]: 'TOKA 4 Tether Case',
    [EquipmentTypes.Toka5BaseStation]: 'TOKA 5 Base Station',
    [EquipmentTypes.Toka5ISENS]: 'TOKA 5 ISENS',
    [EquipmentTypes.Toka5Robot]: 'TOKA 5 Robot',
    [EquipmentTypes.TokaFlexISENS]: 'TOKA FLEX ISENS',
    [EquipmentTypes.TokaFlexRobot]: 'TOKA FLEX Robot',
    [EquipmentTypes.TokaGZISENS]: 'TOKA GZ ISENS',
    [EquipmentTypes.TokaGZRobot]: 'TOKA GZ Robot',
    [EquipmentTypes.TokaMLOTether]: 'TOKA MLO Tether',
    [EquipmentTypes.ToolBox]: 'Tool Box',
    [EquipmentTypes.VoltageTransformerPelican]: 'Voltage Transformer Pelican',
    [EquipmentTypes.ZED]: 'ZED',
  }
  return labels[type] ?? type
}

export enum OfficeLocation {
  Pittsburgh = 'pittsburgh',
  Houston = 'houston',
  UAE = 'uae',
  Boston = 'boston',
  Everett = 'everett',
  Netherlands = 'netherlands',
  NewYork = 'new_york',
  SanDiego = 'san_diego',
  WashingtonDC = 'washington_dc',
  Unknown = 'unknown',
}

export const getOfficeLocation = (value: string) => {
  if (Object.values(OfficeLocation).includes(value as OfficeLocation)) {
    return value as OfficeLocation
  }
  return OfficeLocation.Unknown
}

export const getOfficeLocationLabel = (location: OfficeLocation): string => {
  const labels: Record<OfficeLocation, string> = {
    [OfficeLocation.Pittsburgh]: 'Pittsburgh Office',
    [OfficeLocation.Houston]: 'Houston Office',
    [OfficeLocation.UAE]: 'UAE Office',
    [OfficeLocation.Boston]: 'Boston MA Office',
    [OfficeLocation.Everett]: 'Everett WA Office',
    [OfficeLocation.Netherlands]: 'Netherlands Office',
    [OfficeLocation.NewYork]: 'New York Office',
    [OfficeLocation.SanDiego]: 'San Diego CA Office',
    [OfficeLocation.WashingtonDC]: 'Washington DC Office',
    [OfficeLocation.Unknown]: 'Unknown',
  }
  return labels[location] ?? location
}

export type ShippingDestination = OfficeLocation | 'job_site'

export const SHIPPING_DESTINATIONS: ShippingDestination[] = [
  ...Object.values(OfficeLocation),
  'job_site',
]

export const getShippingDestination = (value: string): ShippingDestination => {
  if (SHIPPING_DESTINATIONS.includes(value as ShippingDestination)) {
    return value as ShippingDestination
  }
  return OfficeLocation.Unknown
}

export const getShippingDestinationLabel = (
  destination: ShippingDestination
): string => {
  if (destination === 'job_site') return 'Job Site'
  return getOfficeLocationLabel(destination as OfficeLocation)
}

export enum AssemblyType {
  APIRadianHead = 'api_radian_head',
  ARG = 'arg',
  BackpackHousing = 'backpack_housing',
  BOFIAccessPoint = 'bofi_access_point',
  BOFIInternetOfTokas = 'bofi_internet_of_tokas',
  CamberSuspension = 'camber_suspension',
  CenterBody = 'center_body',
  DriveModule = 'drive_module',
  EMATBaseStation = 'emat_base_station',
  EMATDaq = 'emat_daq',
  EMATLaptop = 'emat_laptop',
  EMATProbe = 'emat_probe',
  EMATProbeHolder = 'emat_probe_holder',
  EMIMeter = 'emi_meter',
  Encoder = 'encoder',
  HandheldUT38DLPlus = 'handheld_ut_38dl_plus',
  IPCamera = 'ip_camera',
  ISENSPayload = 'isens_payload',
  LightBar = 'light_bar',
  LinearSuspension = 'linear_suspension',
  MagnetismDetector = 'magnetism_detector',
  NavicController = 'navic_controller',
  NavicPowerSupply = 'navic_power_supply',
  NavicRobot = 'navic_robot',
  PowerModule = 'power_module',
  TwelveInchRasterArm = 'twelve_inch_raster_arm',
  TwentyFourInchRasterArm = 'twenty_four_inch_raster_arm',
  RTSHandheld = 'rts_handheld',
  RTSHead = 'rts_head',
  SMR15 = 'smr_1_5',
  SparePartsBoxISENS = 'spare_parts_box_isens',
  SparePartsBoxRobot = 'spare_parts_box_robot',
  SphericalReflector = 'spherical_reflector',
  Tether = 'tether',
  ThicknessGage = 'thickness_gage',
  Toka4DrivingCamera = 'toka4_driving_camera',
  Tripod = 'tripod',
  VisualInspectionModule = 'visual_inspection_module',
  WaterModule = 'water_module',
  VoltageTransformer = 'voltage_transformer',
}

export const getAssemblyType = (value: string) => {
  if (Object.values(AssemblyType).includes(value as AssemblyType)) {
    return value as AssemblyType
  }
  return AssemblyType.Tether
}

export const getAssemblyTypeLabel = (type: AssemblyType): string => {
  const labels: Record<AssemblyType, string> = {
    [AssemblyType.Tether]: 'Tether',
    [AssemblyType.APIRadianHead]: 'API Radian Head',
    [AssemblyType.ARG]: 'ARG',
    [AssemblyType.BackpackHousing]: 'Backpack Housing',
    [AssemblyType.BOFIAccessPoint]: 'BOFI Access Point',
    [AssemblyType.BOFIInternetOfTokas]: 'BOFI Internet of Tokas',
    [AssemblyType.CamberSuspension]: 'Camber Suspension',
    [AssemblyType.CenterBody]: 'Center Body',
    [AssemblyType.DriveModule]: 'Drive Module',
    [AssemblyType.EMATBaseStation]: 'EMAT Base Station',
    [AssemblyType.EMATDaq]: 'EMAT Daq',
    [AssemblyType.EMATLaptop]: 'EMAT Laptop',
    [AssemblyType.EMATProbe]: 'EMAT Probe',
    [AssemblyType.EMATProbeHolder]: 'EMAT Probe Holder',
    [AssemblyType.EMIMeter]: 'EMI Meter',
    [AssemblyType.Encoder]: 'Encoder',
    [AssemblyType.HandheldUT38DLPlus]: 'Handheld UT 38DL Plus',
    [AssemblyType.IPCamera]: 'IP Camera',
    [AssemblyType.ISENSPayload]: 'ISENS Payload',
    [AssemblyType.LightBar]: 'Light Bar',
    [AssemblyType.LinearSuspension]: 'Linear Suspension',
    [AssemblyType.MagnetismDetector]: 'Magnetism Detector',
    [AssemblyType.NavicController]: 'Navic Controller',
    [AssemblyType.NavicPowerSupply]: 'Navic Power Supply',
    [AssemblyType.NavicRobot]: 'Navic Robot',
    [AssemblyType.PowerModule]: 'Power Module',
    [AssemblyType.TwelveInchRasterArm]: '12" Raster Arm',
    [AssemblyType.TwentyFourInchRasterArm]: '24" Raster Arm',
    [AssemblyType.RTSHandheld]: 'RTS Handheld',
    [AssemblyType.RTSHead]: 'RTS Head',
    [AssemblyType.SMR15]: 'SMR 1.5"',
    [AssemblyType.SparePartsBoxISENS]: 'Spare Parts Box (ISENS)',
    [AssemblyType.SparePartsBoxRobot]: 'Spare Parts Box (Robot)',
    [AssemblyType.SphericalReflector]: 'Spherical Reflector',
    [AssemblyType.ThicknessGage]: 'Thickness Gage',
    [AssemblyType.Toka4DrivingCamera]: 'TOKA 4 Driving Camera',
    [AssemblyType.Tripod]: 'Tripod',
    [AssemblyType.VisualInspectionModule]: 'Visual Inspection Module',
    [AssemblyType.WaterModule]: 'Water Module',
    [AssemblyType.VoltageTransformer]: 'Voltage Transformer',
  }
  return labels[type] ?? type
}

export enum InventoryItemConfiguration {
  Raster12Inch = '12_inch_raster',
  Raster24Inch = '24_inch_raster',
  ThreeVIM = '3_vim',
  Camber = 'camber',
  EMAT = 'emat',
  IMU = 'imu',
  Linear = 'linear',
  PAUTLoadout = 'paut_loadout',
  PeelAndStick = 'peel_and_stick',
  RAUTLoadout = 'raut_loadout',
  RollOnNonSkid = 'roll_on_non_skid',
  SDTank = 'sd_tank',
  SDThick = 'sd_thick',
  SDThin = 'sd_thin',
  TriLateralLoadout = 'tri_lateral_loadout',
}

export const getInventoryItemConfiguration = (value: string) => {
  if (
    Object.values(InventoryItemConfiguration).includes(
      value as InventoryItemConfiguration
    )
  ) {
    return value as InventoryItemConfiguration
  }
  return InventoryItemConfiguration.Linear
}

export const getInventoryItemConfigurationLabel = (
  config: InventoryItemConfiguration
): string => {
  const labels: Record<InventoryItemConfiguration, string> = {
    [InventoryItemConfiguration.Camber]: 'Camber',
    [InventoryItemConfiguration.Linear]: 'Linear',
    [InventoryItemConfiguration.ThreeVIM]: '3-VIM',
    [InventoryItemConfiguration.IMU]: 'IMU',
    [InventoryItemConfiguration.SDThin]: 'SD-Thin',
    [InventoryItemConfiguration.SDTank]: 'SD-Tank',
    [InventoryItemConfiguration.SDThick]: 'SD-Thick',
    [InventoryItemConfiguration.PAUTLoadout]: 'PAUT Loadout',
    [InventoryItemConfiguration.RAUTLoadout]: 'RAUT Loadout',
    [InventoryItemConfiguration.TriLateralLoadout]: 'Tri-Lateral Loadout',
    [InventoryItemConfiguration.PeelAndStick]: 'Peel and Stick',
    [InventoryItemConfiguration.RollOnNonSkid]: 'Roll On Non-Skid',
    [InventoryItemConfiguration.EMAT]: 'EMAT',
    [InventoryItemConfiguration.Raster12Inch]: '12" Raster',
    [InventoryItemConfiguration.Raster24Inch]: '24" Raster',
  }
  return labels[config] ?? config
}

export enum InventoryItemStatus {
  InStock = 'in_stock',
  InInventory = 'in_inventory',
  OnJob = 'on_job',
  Transfer = 'transfer',
  Staged = 'staged',
  Planned = 'planned',
  EngineeringSupport = 'engineering_support',
  EngineeringSupportRequested = 'engineering_support_requested',
  ITSupport = 'it_support',
  ITSupportRequested = 'it_support_requested',
  MechTechSupport = 'mech_tech_support',
  MechTechSupportRequested = 'mech_tech_support_requested',
  OperationsSupport = 'operations_support',
  Awaiting3rdPartyRepair = 'awaiting_3rd_party_repair',
  Decommissioned = 'decommissioned',
  ThirdParty = '3rd_party',
}

export const getInventoryItemStatus = (value: string) => {
  if (
    Object.values(InventoryItemStatus).includes(value as InventoryItemStatus)
  ) {
    return value as InventoryItemStatus
  }
  return InventoryItemStatus.Planned
}

export const getInventoryItemStatusLabel = (
  status: InventoryItemStatus
): string => {
  const labels: Record<InventoryItemStatus, string> = {
    [InventoryItemStatus.InStock]: 'In Stock',
    [InventoryItemStatus.InInventory]: 'In Inventory',
    [InventoryItemStatus.OnJob]: 'On Job',
    [InventoryItemStatus.Transfer]: 'Transfer',
    [InventoryItemStatus.Staged]: 'Staged',
    [InventoryItemStatus.Planned]: 'Planned',
    [InventoryItemStatus.EngineeringSupport]: 'Engineering Support',
    [InventoryItemStatus.EngineeringSupportRequested]:
      'Engineering Support Requested',
    [InventoryItemStatus.ITSupport]: 'IT Support',
    [InventoryItemStatus.ITSupportRequested]: 'IT Support Requested',
    [InventoryItemStatus.MechTechSupport]: 'Mech Tech Support',
    [InventoryItemStatus.MechTechSupportRequested]:
      'Mech Tech Support Requested',
    [InventoryItemStatus.OperationsSupport]: 'Operations Support',
    [InventoryItemStatus.Awaiting3rdPartyRepair]: 'Awaiting 3rd Party Repair',
    [InventoryItemStatus.Decommissioned]: 'Decommissioned',
    [InventoryItemStatus.ThirdParty]: '3rd Party',
  }
  return labels[status] ?? status
}

export enum AssemblyVersion {
  V1 = '1',
  V1_0_0_ENG_IP_CAM = '1.0.0_eng_ip_cam',
  V1_2 = '1.2',
  V1_3 = '1.3',
  V1_4_0 = '1.4.0',
  V1_4_0_PILOT = '1.4.0_pilot',
  V2_1 = '2.1_spencer_mod',
  V2_1_0 = '2.1.0',
  V2_1_0_BOARD_DELETE = '2.1.0_board_delete',
  V2_2_0 = '2.2.0',
  V2_2_0_B = '2.2.0_b',
  V2_2_1 = '2.2.1',
  V2_2_1_B = '2.2.1_b',
  V2_2_2 = '2.2.2',
  V2_2_2_B = '2.2.2_b',
  V2_2_3 = '2.2.3',
  V2_2_3_B = '2.2.3_b',
  V3_0_0 = '3.0.0',
  V3_0_0_PILOT = '3.0.0_pilot',
  V4_1 = '4.1',
  V4_1_0 = '4.1.0',
  V4_1_1 = '4.1.1',
  V4_1_LEGACY = '4.1_legacy',
  V5_0_0 = '5.0.0',
  V5_0_0_PILOT = '5.0.0_pilot',
  V5_0_0_PILOT_MEGAVER = '5.0.0_pilot_megaver',
  SON_1035 = 'son_1035',
  SON_1037 = 'son_1037',
  SON_1083 = 'son_1083',
  SON_1084 = 'son_1084',
}

export const getAssemblyVersion = (value: string) => {
  if (Object.values(AssemblyVersion).includes(value as AssemblyVersion)) {
    return value as AssemblyVersion
  }
  return AssemblyVersion.V1
}

export const getAssemblyVersionLabel = (version: AssemblyVersion): string => {
  const labels: Record<AssemblyVersion, string> = {
    [AssemblyVersion.V1]: 'v1',
    [AssemblyVersion.V1_0_0_ENG_IP_CAM]: 'v1.0.0-ENG IP CAM',
    [AssemblyVersion.V1_2]: 'v1.2',
    [AssemblyVersion.V1_3]: 'v1.3',
    [AssemblyVersion.V1_4_0]: 'v1.4.0',
    [AssemblyVersion.V1_4_0_PILOT]: 'v1.4.0-PILOT',
    [AssemblyVersion.V2_1]: 'v2.1 (Spencer Mod)',
    [AssemblyVersion.V2_1_0]: 'v2.1.0',
    [AssemblyVersion.V2_1_0_BOARD_DELETE]: 'v2.1.0 Board Delete',
    [AssemblyVersion.V2_2_0]: 'v2.2.0',
    [AssemblyVersion.V2_2_0_B]: 'v2.2.0-B',
    [AssemblyVersion.V2_2_1]: 'v2.2.1',
    [AssemblyVersion.V2_2_1_B]: 'v2.2.1-B',
    [AssemblyVersion.V2_2_2]: 'v2.2.2',
    [AssemblyVersion.V2_2_2_B]: 'v2.2.2-B',
    [AssemblyVersion.V2_2_3]: 'v2.2.3',
    [AssemblyVersion.V2_2_3_B]: 'v2.2.3-B',
    [AssemblyVersion.V3_0_0]: 'v3.0.0',
    [AssemblyVersion.V3_0_0_PILOT]: 'v3.0.0-PILOT',
    [AssemblyVersion.V4_1]: 'v4.1',
    [AssemblyVersion.V4_1_0]: 'v4.1.0',
    [AssemblyVersion.V4_1_1]: 'v4.1.1',
    [AssemblyVersion.V4_1_LEGACY]: 'v4.1-LEGACY',
    [AssemblyVersion.V5_0_0]: 'v5.0.0',
    [AssemblyVersion.V5_0_0_PILOT]: 'v5.0.0-PILOT',
    [AssemblyVersion.V5_0_0_PILOT_MEGAVER]: 'v5.0.0-PILOT-MEGAVER',
    [AssemblyVersion.SON_1035]: 'SON-1035',
    [AssemblyVersion.SON_1037]: 'SON-1037',
    [AssemblyVersion.SON_1083]: 'SON-1083',
    [AssemblyVersion.SON_1084]: 'SON-1084',
  }
  return labels[version] ?? version
}

export enum TetherVariant {
  Part1038194 = '1038194',
  FRP = 'frp',
  InHouseJoseph = 'in_house_joseph',
  InHouseShattuc = 'in_house_shattuc',
  Original = 'original',
  SAroundFH = 's_around_fh',
  SHAroundF = 'sh_around_f',
  StiffHose = 'stiff_hose',
}

export const getTetherVariant = (value: string) => {
  if (Object.values(TetherVariant).includes(value as TetherVariant)) {
    return value as TetherVariant
  }
  return TetherVariant.Original
}

export const getTetherVariantLabel = (variant: TetherVariant): string => {
  const labels: Record<TetherVariant, string> = {
    [TetherVariant.Part1038194]: 'Part 1038194',
    [TetherVariant.FRP]: 'FRP',
    [TetherVariant.InHouseJoseph]: 'In House (Joseph)',
    [TetherVariant.InHouseShattuc]: 'In House (Shattuc)',
    [TetherVariant.Original]: 'Original',
    [TetherVariant.SAroundFH]: 'S Around FH',
    [TetherVariant.SHAroundF]: 'SH Around F',
    [TetherVariant.StiffHose]: 'Stiff Hose',
  }
  return labels[variant] ?? variant
}
